<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.2 — the Security Center hub: header, one tab per section
// (canvas: Main), and the section below. The two-step tab appears only where
// the two-step feature is on too. Loads the overview once for every tab.
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Spinner from 'shared/components/Spinner.vue';
import {
  useKaptionSecurity,
  resetKaptionSecurityState,
} from 'dashboard/composables/useKaptionSecurity';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentAccount = useMapGetter('getCurrentAccount');
const flagsReady = useMapGetter('kaptionFeatureFlags/isInitialized');
const accountId = useMapGetter('getCurrentAccountId');
const {
  overview,
  loading,
  failed,
  isEnabled,
  twoStepAvailable,
  fetchOverview,
} = useKaptionSecurity();

const tabs = computed(() =>
  [
    {
      name: 'settings_kaption_security',
      label: t('KAPTION_SECURITY.TABS.OVERVIEW'),
    },
    twoStepAvailable.value && {
      name: 'settings_kaption_security_two_step',
      label: t('KAPTION_SECURITY.TABS.TWO_STEP'),
    },
    {
      name: 'settings_kaption_security_sso',
      label: t('KAPTION_SECURITY.TABS.SSO'),
    },
    {
      name: 'settings_kaption_security_domains',
      label: t('KAPTION_SECURITY.TABS.DOMAINS'),
    },
    {
      name: 'settings_kaption_security_users',
      label: t('KAPTION_SECURITY.TABS.USERS'),
    },
    {
      name: 'settings_kaption_security_sessions',
      label: t('KAPTION_SECURITY.TABS.SESSIONS'),
    },
    {
      name: 'settings_kaption_security_activity',
      label: t('KAPTION_SECURITY.TABS.ACTIVITY'),
    },
  ].filter(Boolean)
);

// Opening the URL directly mounts this before the flags have loaded: wait for
// them instead of treating "not loaded yet" as "off".
const leaveIfDisabled = () => {
  if (isEnabled.value || !flagsReady.value) return;
  router.replace({
    name: 'general_settings_index',
    params: { accountId: accountId.value },
  });
};

onMounted(() => {
  leaveIfDisabled();
  fetchOverview({ force: true });
});

// On a narrow screen the tab row scrolls: keep the open tab in view.
const tabsNav = ref(null);
const revealActiveTab = () =>
  nextTick(() =>
    tabsNav.value
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  );
watch(() => route.name, revealActiveTab);
onMounted(revealActiveTab);

watch(accountId, () => {
  resetKaptionSecurityState();
  fetchOverview({ force: true });
});
watch([isEnabled, flagsReady], () => {
  leaveIfDisabled();
  if (isEnabled.value && !overview.value) fetchOverview();
});
</script>

<template>
  <div
    v-if="isEnabled"
    class="flex flex-col w-full max-w-[880px] gap-8 mx-auto"
  >
    <header class="flex flex-col gap-2">
      <h1
        class="flex items-center gap-2 m-0 text-2xl font-semibold text-n-slate-12"
      >
        <span class="i-lucide-shield size-5 text-n-brand" aria-hidden="true" />
        {{ t('KAPTION_SECURITY.TITLE') }}
      </h1>
      <p class="m-0 text-sm text-n-slate-11">
        {{
          t('KAPTION_SECURITY.DESCRIPTION', {
            account: currentAccount?.name || '',
          })
        }}
      </p>
    </header>

    <nav
      ref="tabsNav"
      class="flex gap-1 -mb-4 overflow-x-auto border-b border-n-weak"
      :aria-label="t('KAPTION_SECURITY.TITLE')"
    >
      <router-link
        v-for="tab in tabs"
        :key="tab.name"
        :to="{ name: tab.name, params: { accountId } }"
        class="px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap -mb-px"
        :class="
          route.name === tab.name
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        :aria-current="route.name === tab.name ? 'page' : undefined"
      >
        {{ tab.label }}
      </router-link>
    </nav>

    <div v-if="loading && !overview" class="flex justify-center py-16">
      <Spinner />
    </div>
    <p
      v-else-if="failed && !overview"
      class="px-4 py-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
    >
      {{ t('KAPTION_SECURITY.LOAD_ERROR') }}
    </p>
    <router-view v-else-if="overview" />
  </div>
</template>
